import { defineStore } from 'pinia';
import { ref } from 'vue';
import {isNumber, keys, pick, reduce, remove, values} from 'lodash-es';

import { getWorkingDaysPerYear, addUp } from '@/utils/calculations';

/**
 * This store contains all objects and calculations related to the tariffs.
 */
export const useTariffsStore = defineStore('tariffs', () => {
    const yearly = ref({
        current: null,
        fullTimeWorkingHours: null,
        jobRelatedCosts: {},
        jobRelatedDays: {},
        leaveDays: null,
        livingWages: {
            net: null,
            withoutOverhead: null,
            withCostsAndOverhead: null,
            withIncomeTax: null,
            incomeTax: null,
            socialContributions: {},
            incomeTaxPlusSocialContributions: null,
        },
        publicHolidays: null,
        workDays: null,
        totalWorkDays: null,
        totalWorkDaysMinusDaysOff: null,
        workHoursAdjustedForUnpaidTime: null,
    });
    const hourly = ref({
        jobRelatedCosts: {},
        jobRelatedMinutes: {},
        livingWages: {
            net: null,
            withoutOverhead: null,
            withCostsAndOverhead: null,
            withIncomeTax: null,
            incomeTax: null,
            socialContributions: {},
            incomeTaxPlusSocialContributions: null,
        },
        totalOverheadCosts: null,
    });
    const monthly = ref({
        jobRelatedCosts: {},
        livingWages: {
            net: null,
            withoutOverhead: null,
            withCostsAndOverhead: null,
            withIncomeTax: null,
            incomeTax: null,
            socialContributions: {},
            incomeTaxPlusSocialContributions: null,
        },
    });
    const weekly = ref({
        fullTimeWorkingHours: null,
        jobRelatedHours: {},
        workHours: null,
        workDays: null,
    });
    const daily = ref({
        fullTimeWorkingHours: null,
    });

    const fieldVisibility = ref({
        occupationCosts: {},
    });

    const occupationCostsRowIsVisible = (name) => {
        if (fieldVisibility.value.occupationCosts['equipment'] === false) {
            return false;
        } else if (name === undefined) {
            return true;
        }

        return Boolean(fieldVisibility.value.occupationCosts[name]);
    };

    function updateCountryTariffs(countryResponses, regionResponses) {
        yearly.value.leaveDays =
            countryResponses.countryProfile['legalholidayyear'];
        yearly.value.publicHolidays =
            countryResponses.countryProfile['legalbankholidays_amount'];

        monthly.value.livingWages.net =
            regionResponses.annualLivingWages.data['netlw25typ_tariff'];

        weekly.value.workHours = parseFloat(
            countryResponses.workingHours['normalworkhoursweek']
        );
        weekly.value.workDays = parseFloat(
            countryResponses.workingHours['normalworkdaysweek']
        );
        yearly.value.workDays = weekly.value.workDays * 52.2;

        yearly.value.livingWages.net = monthly.value.livingWages.net * 12;
        hourly.value.livingWages.net =
            monthly.value.livingWages.net / (4.333 * weekly.value.workHours);

        yearly.value.totalWorkDays = getWorkingDaysPerYear(
            yearly.value.current,
            weekly.value.workDays
        );
        yearly.value.totalWorkDaysMinusDaysOff =
            yearly.value.totalWorkDays -
            yearly.value.leaveDays -
            yearly.value.publicHolidays;

        yearly.value.fullTimeWorkingHours =
            yearly.value.totalWorkDaysMinusDaysOff * (weekly.value.workHours / weekly.value.workDays);

        // TODO: remove redundant variable
        weekly.value.fullTimeWorkingHours = weekly.value.workHours;

        daily.value.fullTimeWorkingHours =
            countryResponses.workingHours['normalworkhoursday'];

        yearly.value.livingWages.withoutOverhead = yearly.value.livingWages.net;
        monthly.value.livingWages.withoutOverhead = yearly.value.livingWages.withoutOverhead / 12
        hourly.value.livingWages.withoutOverhead =
            yearly.value.livingWages.net / yearly.value.fullTimeWorkingHours;
    }

    function updateTariffs(
        countryResponses,
        regionResponses,
        occupationResponses
    ) {
        if (occupationResponses.gigData) {
            // Clear previously stored values
            yearly.value.jobRelatedCosts = {};
            monthly.value.jobRelatedCosts = {};
            hourly.value.jobRelatedCosts = {};

            const occupationCostVisibleFields = {
                equipment: 'costs_for_equipment',
                computer: 'cost_computer_yn',
                phone: 'cost_phone_yn',
                car: 'cost_car_yn',
                scooter: 'cost_scooter_yn',
                electricBicycle: 'cost_ebicycle_yn',
                bicycle: 'cost_bicycle_yn',
            };
            const occupationCostFields = {
                computer: 'computer_wifi_costs',
                phone: 'phone_costs',
                car: 'car_costs',
                scooter: 'scooter_costs',
                electricBicycle: 'electric_bicycle_costs',
                bicycle: 'bicycle_costs',
            };

            for (const [key, value] of Object.entries(
                occupationCostVisibleFields
            )) {
                let isVisible = false;
                let isVisibleValue = occupationResponses.gigData[value];
                if (isVisibleValue) {
                    isVisible = isVisibleValue.trim() === '1';
                }

                if (isVisible === false) {
                    delete occupationCostFields[key];
                }
                fieldVisibility.value.occupationCosts[key] = isVisible;
            }

            for (const [key, value] of Object.entries(occupationCostFields)) {
                const monthlyCosts =  countryResponses.occupationMonthlyCosts[value];
                if (isNumber(monthlyCosts)) {
                    yearly.value.jobRelatedCosts[key] =
                        monthlyCosts * 12;
                    monthly.value.jobRelatedCosts[key] =
                        monthlyCosts;
                    hourly.value.jobRelatedCosts[key] =
                        (monthlyCosts * 12 ) / yearly.value.fullTimeWorkingHours
                }
                console.log(key, monthly.value.jobRelatedCosts[key])

            }

            yearly.value.jobRelatedCosts['total'] = reduce(
                values(
                    pick(
                        yearly.value.jobRelatedCosts,
                        keys(occupationCostFields)
                    )
                ),
                addUp,
                0
            );
            monthly.value.jobRelatedCosts['total'] = reduce(
                values(
                    pick(
                        monthly.value.jobRelatedCosts,
                        keys(occupationCostFields)
                    )
                ),
                addUp,
                0
            );
            hourly.value.jobRelatedCosts['total'] = reduce(
                values(
                    pick(
                        hourly.value.jobRelatedCosts,
                        keys(occupationCostFields)
                    )
                ),
                addUp,
                0
            );

            const jobRelatedTimeFields = {
                administration: 'days_per_year_needed_for_administration',
                acquisition:
                    'days_per_year_needed_for_acquisition_communication',
                training: 'days_per_year_needed_for_training',
                waiting: 'waiting_time_in_minutes_per_hour',
            };

            // Clear previously stored values
            yearly.value.jobRelatedDays = {};
            weekly.value.jobRelatedHours = {}
            hourly.value.jobRelatedMinutes = {}

            for (const [key, value] of Object.entries(jobRelatedTimeFields)) {
                let gigValue = parseInt(occupationResponses.gigData[value]);
                let yearlyValue;
                let hourlyValue;

                if (value === 'waiting_time_in_minutes_per_hour') {
                    yearlyValue =
                        (gigValue * yearly.value.fullTimeWorkingHours) /
                        (daily.value.fullTimeWorkingHours * 60);
                    hourlyValue = gigValue;
                } else {
                    yearlyValue = gigValue;
                    hourlyValue =
                        (yearlyValue * daily.value.fullTimeWorkingHours * 60) /
                        yearly.value.fullTimeWorkingHours;
                }

                // yearly.value.jobRelatedDays[key] = +yearlyValue.toFixed(0);
                if (isNumber(yearlyValue)) {
                    yearly.value.jobRelatedDays[key] = yearlyValue;
                    weekly.value.jobRelatedHours[key] =
                        +((yearlyValue * daily.value.fullTimeWorkingHours) / 52.2).toFixed(1);
                }
                if (isNumber(+hourlyValue)) {
                    hourly.value.jobRelatedMinutes[key] = +hourlyValue.toFixed(1);
                }
            }

            yearly.value.jobRelatedDays['total'] = reduce(
                values(
                    pick(
                        yearly.value.jobRelatedDays,
                        keys(jobRelatedTimeFields)
                    )
                ),
                addUp,
                0
            );
            weekly.value.jobRelatedHours['total'] = reduce(
                values(
                    pick(
                        weekly.value.jobRelatedHours,
                        keys(jobRelatedTimeFields)
                    )
                ),
                addUp,
                0
            );
            hourly.value.jobRelatedMinutes['total'] = reduce(
                values(
                    pick(
                        hourly.value.jobRelatedMinutes,
                        keys(jobRelatedTimeFields)
                    )
                ),
                addUp,
                0
            );
        }

        // hourly.value.totalOverheadCosts =
        //     (hourly.value.jobRelatedMinutes.total / 60) *
        //     (hourly.value.livingWages.withoutOverhead +
        //         hourly.value.jobRelatedCosts['total']);

        hourly.value.totalOverheadCosts =
            (weekly.value.jobRelatedHours.total * 52.2 / yearly.value.fullTimeWorkingHours ) *
            (hourly.value.livingWages.withoutOverhead +
                hourly.value.jobRelatedCosts.total);

        yearly.value.livingWages.withCostsAndOverhead =
            yearly.value.livingWages.withoutOverhead +
            yearly.value.jobRelatedCosts['total'];

        monthly.value.livingWages.withCostsAndOverhead =
            yearly.value.livingWages.withCostsAndOverhead / 12;

        yearly.value.workHoursAdjustedForUnpaidTime =
            (yearly.value.totalWorkDaysMinusDaysOff - yearly.value.jobRelatedDays['total']) *
            weekly.value.fullTimeWorkingHours / weekly.value.workDays;

        hourly.value.livingWages.withCostsAndOverhead =
            hourly.value.livingWages.withoutOverhead +
            hourly.value.jobRelatedCosts['total'] +
            hourly.value.totalOverheadCosts;

        if (countryResponses.socialSecurityTaxes.withOverhead) {
            const incomeTax =
                countryResponses.socialSecurityTaxes.withOverhead['income_tax'];

            yearly.value.livingWages.incomeTax = incomeTax;
            monthly.value.livingWages.incomeTax = incomeTax / 12;

            hourly.value.livingWages.incomeTax =
                incomeTax / yearly.value.fullTimeWorkingHours;

            for (const [key, value] of Object.entries(
                countryResponses.socialSecurityTaxes.withOverhead['social_contributions']
            )) {
                yearly.value.livingWages.socialContributions[key] = value;
                monthly.value.livingWages.socialContributions[key] = value / 12;
                hourly.value.livingWages.socialContributions[key] =
                    value / yearly.value.fullTimeWorkingHours;
            }
        }

        let medicalInsuranceAmount =
            regionResponses.quarterlyLivingWages.data['s25_hinf10'];
        medicalInsuranceAmount =
            medicalInsuranceAmount === null ? 0 : medicalInsuranceAmount;
        yearly.value.livingWages.socialContributions['medical_insurance'] =
            medicalInsuranceAmount;
        monthly.value.livingWages.socialContributions['medical_insurance'] =
            medicalInsuranceAmount / 12;
        hourly.value.livingWages.socialContributions['medical_insurance'] =
            medicalInsuranceAmount / (12 * 4.333 * weekly.value.workHours);

        const socialContributionsFields = remove(
            keys(hourly.value.livingWages.socialContributions),
            function (f) {
                return f !== 'total';
            }
        );

        yearly.value.livingWages.socialContributions['total'] = reduce(
            values(
                pick(
                    yearly.value.livingWages.socialContributions,
                    socialContributionsFields
                )
            ),
            addUp
        );
        monthly.value.livingWages.socialContributions['total'] = reduce(
            values(
                pick(
                    monthly.value.livingWages.socialContributions,
                    socialContributionsFields
                )
            ),
            addUp
        );
        hourly.value.livingWages.socialContributions['total'] = reduce(
            values(
                pick(
                    hourly.value.livingWages.socialContributions,
                    socialContributionsFields
                )
            ),
            addUp
        );

        yearly.value.livingWages.incomeTaxPlusSocialContributions =  yearly.value.livingWages.incomeTax +
            yearly.value.livingWages.socialContributions['total']
        yearly.value.livingWages.withIncomeTax =
            yearly.value.livingWages.withCostsAndOverhead +
            yearly.value.livingWages.incomeTaxPlusSocialContributions;

        monthly.value.livingWages.incomeTaxPlusSocialContributions =
            yearly.value.livingWages.incomeTaxPlusSocialContributions / 12
        monthly.value.livingWages.withIncomeTax =
            yearly.value.livingWages.withIncomeTax / 12;

        hourly.value.livingWages.incomeTaxPlusSocialContributions = hourly.value.livingWages.incomeTax +
            hourly.value.livingWages.socialContributions['total'];


        hourly.value.livingWages.withIncomeTax =
            hourly.value.livingWages.withCostsAndOverhead +
            hourly.value.livingWages.incomeTaxPlusSocialContributions;
    }

    return {
        yearly,
        hourly,
        monthly,
        weekly,
        occupationCostsRowIsVisible,
        updateCountryTariffs,
        updateTariffs,
    };
});
